import { memo } from 'react';

import Select from 'react-select';

import isEqual from 'fast-deep-equal';

import { isDefined } from '@enterprise/core';

import { DEFAULT_PAGE_SIZE_OPTIONS } from '#pagination/constants/default-page-size-options';

import type { PageSizesProps } from './types/page-sizes.props';

import { usePageSizesConfiguration } from './hooks/page-sizes-configuration.hook';

import cssClasses from './page-size.module.css';

/**
 * Renders a page size selector component for pagination controls.
 *
 * @remarks
 * This component displays a dropdown allowing users to select the number of items per page.
 * It uses `react-select` for the dropdown UI and supports custom page size options.
 * The component is memoized for performance optimization, using deep equality checks.
 *
 * @param props - The props for the PageSize component.
 * @param props.onPageSizeChanged - Callback invoked when the page size is changed.
 * @param props.pageSize - The currently selected page size.
 * @param props.pageSizes - Optional array of page size options to display; defaults to `DEFAULT_PAGE_SIZE_OPTIONS`.
 *
 * @returns A React element rendering the page size selector.
 */
function PageSize({
  disabled = false,
  onPageSizeChanged,
  pageSize,
  pageSizes = DEFAULT_PAGE_SIZE_OPTIONS,
}: PageSizesProps) {
  const { pageSizeOptions } = usePageSizesConfiguration({ pageSizes });

  return (
    <div className={cssClasses.itemsPerPage}>
      <div>Items per page:</div>
      <div className="ms-2">
        <Select
          className={cssClasses.paginationSelect}
          classNamePrefix="pagination-select"
          isDisabled={disabled}
          onChange={(option) => {
            if (isDefined(option)) {
              onPageSizeChanged?.(option.value);
            }
          }}
          options={pageSizeOptions}
          value={pageSizeOptions.find((option) => {
            return option.value === pageSize;
          })}
        />
      </div>
    </div>
  );
}

export default memo(PageSize, (prev, next) => {
  return isEqual(prev, next);
}) as typeof PageSize;
